import {CurrentProject} from 'common/report/queries/app-api/project-metadata-client';
import {ProjectDetails} from 'common/report/stores/project-details';
import {REPORT_PERSISTENCE_COOKIE} from 'common/report/constants';
import {getWorkspaceId, isPublicMode} from 'common/report/util';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {ReportPersistenceCookie} from 'common/widgets/chrome/chrome-header/types';
import {getCookie} from 'common/util/cookie';
import {WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {sortWorkspaces} from 'common/util/workspaces';
import type {ProjectsResource} from 'common/report/model-resources/projects';
import {isProjectOnlyApp} from 'common/report/util/state-from-url';
import {isTestEnv} from 'common/report/util/env';

export function isGlobalSidenavEnabled(_currentProject?: CurrentProject | ProjectDetails): boolean {
  return !isTestEnv();
}

export function useIsGlobalSidenavEnabled() {
  const currentProject = isPublicMode() ? undefined : useCurrentProject().get().value;
  return isGlobalSidenavEnabled(currentProject);
}

export function isGsNewSearchSplitEnabled(_currentProject?: CurrentProject | ProjectDetails): boolean {
  return !isTestEnv();
}

export function useIsGsNewSearchSplitEnabled() {
  const currentProject = isPublicMode() ? undefined : useCurrentProject().get().value;
  return isGsNewSearchSplitEnabled(currentProject);
}

export function getWorkspacesForProject({
  projectsResource,
  currentProject,
  projectId,
}: {
  projectsResource?: ProjectsResource;
  currentProject?: CurrentProject;
  projectId: number;
}): WorkspaceObj[] {
  const project = projectId === currentProject?.id ? currentProject : projectsResource?.getOne(projectId).value;
  return Object.values(project?.workspaces ?? {}).sort(sortWorkspaces);
}

export function getWorkspacesForCurrentProject({currentProject}: {currentProject?: CurrentProject}): WorkspaceObj[] {
  return currentProject ? getWorkspacesForProject({currentProject, projectId: currentProject.id}) : [];
}

function getValidWorkspaceId({
  currentProject,
  workspaceIdPriorityList,
}: {
  currentProject?: CurrentProject;
  workspaceIdPriorityList: number[];
}): number | undefined {
  const userWorkspaces = getWorkspacesForCurrentProject({currentProject});
  for (const workspaceId of workspaceIdPriorityList) {
    const found = userWorkspaces.find((workspace) => workspace.id === workspaceId);
    if (found) {
      return found.id;
    }
  }
  return userWorkspaces.length ? userWorkspaces[0].id : undefined;
}

function getGlobalWorkspaceIdForProject({currentProject}: {currentProject: CurrentProject}): number | undefined {
  const projectWorkspaces = getWorkspacesForCurrentProject({currentProject});
  const globalWorkspace = projectWorkspaces.find((workspace) => workspace.isGlobal);
  return globalWorkspace ? globalWorkspace.id : undefined;
}

function getWorkspaceIdFromPersistenceCookie({projectId}: {projectId: number}): number | undefined {
  try {
    const persistenceCookie: ReportPersistenceCookie = JSON.parse(getCookie(REPORT_PERSISTENCE_COOKIE) || `{}`);
    if (persistenceCookie.workspace && persistenceCookie.project === projectId) {
      return persistenceCookie.workspace;
    }
  } catch {
    /* fail gracefully*/
  }
  return undefined;
}

export function findCurrentValidWorkspaceId({
  currentProject,
  firstPriorityWorkspaceId,
}: {
  currentProject?: CurrentProject;
  firstPriorityWorkspaceId: number | undefined;
}): number | undefined {
  if (!currentProject) {
    return undefined;
  }
  return getValidWorkspaceId({
    currentProject,
    workspaceIdPriorityList: [
      firstPriorityWorkspaceId,
      getWorkspaceIdFromPersistenceCookie({projectId: currentProject.id}),
      getGlobalWorkspaceIdForProject({currentProject}),
    ].filter((id): id is number => typeof id === `number`),
  });
}

export function getCurrentWorkspaceId({
  currentProject,
  reportEndpoint,
}: {
  currentProject?: CurrentProject;
  reportEndpoint?: string;
}): number | undefined {
  // rely on the url for the current workspace id
  let currentWorkspaceId = parseInt(getWorkspaceId(), 10) || undefined; // undefined instead of NaN

  if (!currentWorkspaceId && isProjectOnlyApp(reportEndpoint)) {
    // project only apps (e.g. settings, data views) never have the workspace id in the url; for these apps we add
    // this special logic to obtain a valid workspace id from outside the url
    currentWorkspaceId = findCurrentValidWorkspaceId({currentProject, firstPriorityWorkspaceId: currentWorkspaceId});
  }

  return currentWorkspaceId;
}
